/**
 * Internal icons.
 *
 * These replace the text glyphs the design uses (✕ ▾ ‹ › ✓). Three reasons,
 * and the third is the one that matters here:
 *
 * 1. A glyph renders in whatever font happens to be loaded, so its weight and
 *    baseline drift between machines — and this product ships an Arabic face
 *    whose metrics differ from the Latin fallback.
 * 2. A glyph is text, so it lands in the accessible name of whatever contains
 *    it. Every icon below is `aria-hidden`; the control carries the name.
 * 3. Directional glyphs do not reliably mirror. `‹` is a quotation mark, not
 *    an arrow, and bidi reordering moves it in ways that depend on the
 *    surrounding text. An SVG with `rtl:rotate-180` flips deterministically.
 *
 * They are internal — not exported from the package — because an app should
 * bring its own icon set rather than inherit five shapes from us.
 */
import { cn } from "./cn";

type IconProps = { className?: string };

const base = "pointer-events-none flex-none";

export function CloseIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3", className)} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

export function ChevronDownIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3", className)} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6.5L8 10.5L12 6.5" />
    </svg>
  );
}

export function CheckIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3", className)} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 8.5L6.5 12L13 4.5" />
    </svg>
  );
}

/** Points toward the start edge — left in LTR, right in RTL. */
export function ChevronStartIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={`${base} size-3 rtl:rotate-180 ${className ?? ""}`} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 3.5L5.5 8L10 12.5" />
    </svg>
  );
}

/** Points toward the end edge — right in LTR, left in RTL. */
export function ChevronEndIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={`${base} size-3 rtl:rotate-180 ${className ?? ""}`} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 3.5L10.5 8L6 12.5" />
    </svg>
  );
}

export function MoreIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3.5", className)} fill="currentColor">
      <circle cx="3.5" cy="8" r="1.5" />
      <circle cx="8" cy="8" r="1.5" />
      <circle cx="12.5" cy="8" r="1.5" />
    </svg>
  );
}

export function PlusIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3.5", className)} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <path d="M8 3.5v9M3.5 8h9" />
    </svg>
  );
}

export function MoonIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3", className)} fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 9.5A5.5 5.5 0 0 1 6.5 2.5a5.5 5.5 0 1 0 7 7z" />
    </svg>
  );
}

export function SunIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3", className)} fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round">
      <circle cx="8" cy="8" r="3" />
      <path d="M8 1v1.5M8 13.5V15M15 8h-1.5M2.5 8H1M12.9 3.1l-1 1M4.1 11.9l-1 1M12.9 12.9l-1-1M4.1 4.1l-1-1" />
    </svg>
  );
}

/**
 * Solid triangle, matching the design's `▾` / `▸` text glyphs.
 *
 * A stroked chevron reads noticeably lighter than a filled triangle at 9px,
 * which is what the design uses for every disclosure caret. Rotate it with
 * `-rotate-90 rtl:rotate-90` for the collapsed state.
 */
export function CaretDownIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 10 10" aria-hidden="true" className={cn(base, "size-[5px]", className)} fill="currentColor">
      <path d="M0 1h10L5 9z" />
    </svg>
  );
}

/** Small solid diamond — the design's note marker beside an order title. */
export function DiamondIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 10 10" aria-hidden="true" className={cn(base, "size-2.5", className)} fill="currentColor">
      <path d="M5 1.2 8.8 5 5 8.8 1.2 5z" />
    </svg>
  );
}

/**
 * The two list-toolbar glyphs, as SVG.
 *
 * The design types them: `\u2630` for the filter view and `\u2699` for display
 * options, at 13px on `--muted`. Drawn shapes were substituted here for the
 * reason at the top of this file — but the SHAPES are the glyphs' own. An
 * earlier pass invented a funnel and a sun instead, which is a different
 * toolbar: `\u2630` is three EQUAL rules, not a tapering filter, and `\u2699` is a cog,
 * not a brightness control.
 *
 * 12px, so each sits in the 11.66px advance box the design's 13px glyph
 * occupies, and the pair lands on the design's pixels with the same 14px gap.
 */

/** `\u2630` — three equal rules. The design titles it "Filter". */
export function ListIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3", className)} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 2h14M1 8h14M1 14h14" />
    </svg>
  );
}

/** `\u2699` — an eight-tooth cog. The design titles it "Display". */
export function SettingsIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3", className)} fill="currentColor" fillRule="evenodd">
      <path d="M8.73 0.74 L10.11 1.01 L10.60 3.15 L11.48 3.74 L13.65 3.38 L14.43 4.55 L13.27 6.41 L13.47 7.45 L15.26 8.73 L14.99 10.11 L12.85 10.60 L12.26 11.48 L12.62 13.65 L11.45 14.43 L9.59 13.27 L8.55 13.47 L7.27 15.26 L5.89 14.99 L5.40 12.85 L4.52 12.26 L2.35 12.62 L1.57 11.45 L2.73 9.59 L2.53 8.55 L0.74 7.27 L1.01 5.89 L3.15 5.40 L3.74 4.52 L3.38 2.35 L4.55 1.57 L6.41 2.73 L7.45 2.53 Z M10.50 8.00 A2.5 2.5 0 1 0 5.50 8.00 A2.5 2.5 0 1 0 10.50 8.00 Z" />
    </svg>
  );
}

/**
 * An arrow leaving its box — the mark on a label that opens somewhere else.
 *
 * Mirrored with `-scale-x-100` rather than rotated: on a mirrored page the
 * arrow should still point UP and away, not down.
 */
export function ArrowOutIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-2.5 rtl:-scale-x-100", className)} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 11 11 5M6 5h5v5" />
    </svg>
  );
}


/**
 * Row actions, taken from the design's own paths (Northloom App v2 canvas,
 * areas table). They live here rather than in an app because an icon redrawn
 * per screen is an icon that drifts — the reason this module is exported.
 */
export function EditIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3.5", className)} fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11.3 2.7a1.6 1.6 0 0 1 2.3 2.3L5.5 13H3v-2.5Z" />
    </svg>
  );
}

export function TrashIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3.5", className)} fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2.5 4.5h11M5.5 4.5V3a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1.5M4 4.5 4.8 13a1 1 0 0 0 1 .9h4.4a1 1 0 0 0 1-.9l.8-8.5" />
    </svg>
  );
}

/** The design's magnifier (Northloom App v2, areas toolbar). */
export function SearchIcon({ className }: IconProps) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className={cn(base, "size-3.5", className)} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
      <path d="M6.8 2.8a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM13.2 13.2 9.8 9.8" />
    </svg>
  );
}
